import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/AppShell';
import VerifiedBadge from '../components/VerifiedBadge';
import api from '../api';
import { useAuth } from '../context/AuthContext';

/**
 * Phase 9 — Trading Adrenaline / Leaderboards
 *
 * Two tabs (MVP scope per user approval):
 *   - Weekly PnL      (realized-only, resets every Monday 00:00 UTC)
 *   - All-Time Volume (buy total_pay + sell amount_received)
 *
 * Header cards:
 *   - Your rank (both leaderboards)
 *   - Your streak days
 *   - Your badge count
 */

const TABS = [
  { key: 'weekly_pnl', label: 'Weekly PnL', endpoint: '/leaderboards/weekly-pnl', metric: 'weekly_realized_pnl', unit: 'pts' },
  { key: 'all_time_volume', label: 'All-Time Volume', endpoint: '/leaderboards/all-time-volume', metric: 'all_time_volume', unit: 'pts' },
];

const fmtPts = (n) => {
  const v = Number(n) || 0;
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M`;
  if (abs >= 10_000) return `${(v / 1_000).toFixed(1)}K`;
  return v.toFixed(2);
};

function RankMedal({ rank }) {
  if (rank === 1) return <span className="lb-medal gold" aria-label="1st">🥇</span>;
  if (rank === 2) return <span className="lb-medal silver" aria-label="2nd">🥈</span>;
  if (rank === 3) return <span className="lb-medal bronze" aria-label="3rd">🥉</span>;
  return <span className="lb-rank-num">#{rank}</span>;
}

function LeaderRow({ row, tab, isMe }) {
  const navigate = useNavigate();
  const value = row[tab.metric] || 0;
  const positive = value >= 0;
  const grad = row.gradient || ['#00B4FF', '#E040FB'];
  return (
    <button
      type="button"
      className={`lb-row${isMe ? ' me' : ''}`}
      onClick={() => row.handle && navigate(`/creator/${row.handle}`)}
      data-testid={`lb-row-${row.rank}`}
    >
      <div className="lb-rank"><RankMedal rank={row.rank} /></div>
      <div
        className="lb-avatar"
        style={{ background: `linear-gradient(135deg, ${grad[0]}, ${grad[1]})` }}
      >
        {row.avatar ? <img src={row.avatar} alt="" /> : <span>{row.initials}</span>}
      </div>
      <div className="lb-identity">
        <div className="lb-name">
          {row.display_name}
          {row.verified && <VerifiedBadge size="sm" />}
          {isMe && <span className="lb-you-tag">You</span>}
        </div>
        <div className="lb-handle">@{row.handle || '—'}</div>
      </div>
      <div className="lb-metrics">
        <div className={`lb-value ${tab.key === 'weekly_pnl' ? (positive ? 'up' : 'down') : ''}`}>
          {tab.key === 'weekly_pnl' && positive && value > 0 ? '+' : ''}
          {fmtPts(value)} <span className="lb-unit">{tab.unit}</span>
        </div>
        <div className="lb-sub">
          {row.trade_count} trade{row.trade_count === 1 ? '' : 's'}
          {row.trade_streak_days > 0 && <> · 🔥 {row.trade_streak_days}d</>}
        </div>
      </div>
    </button>
  );
}

function BadgeChip({ badge, unlocked }) {
  return (
    <div
      className={`lb-badge${unlocked ? ' unlocked' : ' locked'}`}
      data-testid={`badge-${badge.key}`}
      title={badge.description}
    >
      <div className="lb-badge-emoji">{badge.emoji}</div>
      <div className="lb-badge-label">{badge.label}</div>
    </div>
  );
}

export default function Leaderboards() {
  const [tab, setTab] = useState(TABS[0].key);
  const [rows, setRows] = useState(null);
  const [meRank, setMeRank] = useState(null);
  const [badgesData, setBadgesData] = useState(null);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  const activeTab = TABS.find((t) => t.key === tab) || TABS[0];

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    api.get(activeTab.endpoint, { params: { limit: 100 } })
      .then(({ data }) => { if (!cancelled) setRows(data.leaderboard || []); })
      .catch(() => { if (!cancelled) setError('Could not load the leaderboard. Try again in a moment.'); });
    return () => { cancelled = true; };
  }, [tab, activeTab.endpoint]);

  useEffect(() => {
    let cancelled = false;
    api.get('/leaderboards/me')
      .then(({ data }) => { if (!cancelled) setMeRank(data); })
      .catch(() => {});
    api.get('/badges/mine')
      .then(({ data }) => { if (!cancelled) setBadgesData(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const myRankForTab = tab === 'weekly_pnl' ? meRank?.weekly_rank : meRank?.volume_rank;
  const myMetricForTab = tab === 'weekly_pnl' ? meRank?.weekly_realized_pnl : meRank?.all_time_volume;

  const myBadgeKeys = new Set((badgesData?.badges || []).map((b) => b.key));

  return (
    <AppShell activeTab="leaders">
      <div className="lb-wrap" data-testid="leaderboards-page">
        <div className="lb-header">
          <h1 className="lb-title" data-testid="leaderboards-title">
            🏆 Leaderboards
          </h1>
          <p className="lb-subtitle">
            Trade to climb — new rankings every Monday, badges award instantly.
          </p>
        </div>

        {/* How this works — expandable explainer for leaderboards + badges criteria. */}
        <details className="lb-explainer" data-testid="lb-explainer">
          <summary className="lb-explainer-summary">
            <span aria-hidden="true">ℹ️</span> How this works
          </summary>
          <div className="lb-explainer-body">
            <div className="lb-explainer-block">
              <div className="lb-explainer-title">Weekly PnL</div>
              <p>
                Sum of your <b>realized</b> profit and loss on trades since Monday
                00:00 UTC. Resets every week. Sell a Bit for more than you bought
                it to add to your PnL — unsold holdings don't count.
              </p>
            </div>
            <div className="lb-explainer-block">
              <div className="lb-explainer-title">All-Time Volume</div>
              <p>
                Cumulative Points spent buying + received selling across every
                Clan you've ever traded. Never resets.
              </p>
            </div>
            <div className="lb-explainer-block">
              <div className="lb-explainer-title">Badges</div>
              <p>
                Achievements are <b>permanent</b> — once you earn one, it stays on
                your profile forever. Most badges (First Blood, Diamond Hands,
                Whale Tamer) are earned <b>per-Clan</b>; some (Deep Pockets,
                Streaker, Top 10) are global across your account. All badges are
                visible to anyone who opens your profile.
              </p>
            </div>
          </div>
        </details>

        {/* Personal stats strip */}
        <div className="lb-me-strip" data-testid="lb-me-strip">
          <div className="lb-me-card">
            <div className="lb-me-value" data-testid="lb-me-rank">
              {myRankForTab ? `#${myRankForTab}` : '—'}
            </div>
            <div className="lb-me-label">Your rank ({activeTab.label})</div>
          </div>
          <div className="lb-me-card">
            <div className="lb-me-value" data-testid="lb-me-metric">
              {myMetricForTab != null
                ? `${tab === 'weekly_pnl' && myMetricForTab > 0 ? '+' : ''}${fmtPts(myMetricForTab)}`
                : '—'}
              <span className="lb-unit">{activeTab.unit}</span>
            </div>
            <div className="lb-me-label">Your total</div>
          </div>
          <div className="lb-me-card">
            <div className="lb-me-value" data-testid="lb-me-streak">
              🔥 {meRank?.trade_streak_days || 0}
              <span className="lb-unit">day{meRank?.trade_streak_days === 1 ? '' : 's'}</span>
            </div>
            <div className="lb-me-label">Trade streak</div>
          </div>
          <div className="lb-me-card">
            <div className="lb-me-value" data-testid="lb-me-badges">
              🏅 {badgesData?.count || 0}
              <span className="lb-unit">/ {(badgesData?.catalog || []).length}</span>
            </div>
            <div className="lb-me-label">Badges unlocked</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="lb-tabs" data-testid="lb-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`lb-tab${tab === t.key ? ' selected' : ''}`}
              onClick={() => setTab(t.key)}
              data-testid={`lb-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Leaderboard body */}
        {error && (
          <div className="empty-state" data-testid="lb-error">
            <p>{error}</p>
          </div>
        )}
        {!error && rows === null && (
          <div className="lb-loading" data-testid="lb-loading">Loading leaderboard…</div>
        )}
        {!error && rows && rows.length === 0 && (
          <div className="lb-empty" data-testid="lb-empty">
            <div className="lb-empty-emoji">🏁</div>
            <div className="lb-empty-title">No traders on the board yet</div>
            <p className="lb-empty-sub">
              {tab === 'weekly_pnl'
                ? 'Sell Bits at a profit this week to appear here.'
                : 'Start trading to appear on the all-time leaderboard.'}
            </p>
          </div>
        )}
        {!error && rows && rows.length > 0 && (
          <div className="lb-list" data-testid="lb-list">
            {rows.map((r) => (
              <LeaderRow
                key={`${tab}-${r.user_id}`}
                row={r}
                tab={activeTab}
                isMe={r.user_id === user?.id}
              />
            ))}
          </div>
        )}

        {/* Badges section */}
        <div className="lb-section-head">
          <h2 className="lb-section-title">🏅 Your badges</h2>
          <span className="lb-section-sub">
            {badgesData?.count || 0} of {(badgesData?.catalog || []).length} unlocked
          </span>
        </div>
        <div className="lb-badges-grid" data-testid="lb-badges-grid">
          {(badgesData?.catalog || []).map((b) => (
            <BadgeChip
              key={b.key}
              badge={b}
              unlocked={myBadgeKeys.has(b.key)}
            />
          ))}
        </div>
      </div>
    </AppShell>
  );
}
